import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getFingerprint } from '@thumbmarkjs/thumbmarkjs';
import { useMainStore } from '@/stores/MainStore.js';
import { useLoginRedirect } from '@/components/login-redirect.js';
const baseUrl = import.meta.env.VITE_API_URL;
const visitorIdStorageKey = 'visitorId';
const SESSION_INVALIDATED_CODE = 1016;

let visitorIdPromise = null;

const getStoredVisitorId = () => window.localStorage.getItem(visitorIdStorageKey);

const storeVisitorId = (visitorId) => {
  if (visitorId) {
    window.localStorage.setItem(visitorIdStorageKey, visitorId);
  }

  return visitorId;
};

const generateVisitorId = () => {
  if (!visitorIdPromise) {
    visitorIdPromise = getFingerprint(false)
      .then(storeVisitorId)
      .catch(() => null);
  }

  return visitorIdPromise;
};

const getAvailableVisitorId = () => {
  const visitorId = getStoredVisitorId();

  if (!visitorId) {
    generateVisitorId();
  }

  return visitorId;
};

export const resolveVisitorId = async () => getStoredVisitorId() || generateVisitorId();

window.regenerateCancelTokenSource = function() {
  window.cancelTokenSource = axios.CancelToken.source();
};

class AxiosClient {
  constructor() {
    this.router = null;
    this.sessionInvalidatedLogoutPending = false;
    this.axiosInstance = axios.create({
      baseURL: baseUrl + '/api/v1/', // Replace with your base URL
      headers: {},
    });

    this.insertAuthCredentials();
    this.makeRequestsCancellable();

    this.handleContext();
    this.handleCorrectResponse();

    this.handleUnauthenticated();
    this.handleForbiddenNotFound();
    this.handleRatelimitExceeded();
    this.handleBandwidthExceeded();
  }

  setRouter(router) {
    this.router = router;
  }

  insertAuthCredentials() {
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        try {
          let session = await fetchAuthSession();

          if (session && session.tokens) {
            config.headers['id-token'] = session.tokens.idToken.toString();
            config.headers['access-token'] = session.tokens.accessToken.toString();

            const visitorId = getAvailableVisitorId();

            if (visitorId) {
              config.headers['x-visitor-id'] = visitorId;
            }
          }
        } catch {
          // Fallthrough
        }

        return config;
      }
    );
  }

  makeRequestsCancellable() {
    window.regenerateCancelTokenSource();

    this.axiosInstance.interceptors.request.use((request) => {
      request.cancelToken = window.cancelTokenSource.token;
      return request;
    });
  }

  handleContext() {
    this.axiosInstance.interceptors.response.use((data) => {
      if (
        data &&
        data.data &&
        data.data.context &&
        data.data.context.rootnodeid &&
        data.data.context.rootnodeid != window.localStorage.contextnodeid
      ) {
        // store the context node and use it later to evaluate the permissions
        // the context node id is also updated in the AuthStore.js file
        window.localStorage.contextnodeid = data.data.context.rootnodeid;
      }

      return Promise.resolve(data);
    });
  }

  handleCorrectResponse() {
    this.axiosInstance.interceptors.response.use((response) => {
      if (response && response.data) {
        return Promise.resolve(response.data);
      }

      return Promise.resolve(response);
    });
  }

  // register interceptor for responses with status 401
  handleUnauthenticated() {
    this.axiosInstance.interceptors.response.use(undefined, (error) => {
      if (error?.response?.status === 401) {
        if (!this.router) return Promise.reject(error);

        const { setLocation } = useLoginRedirect();
        const errorCode = error?.response?.data?.error?.code;

        if (this.sessionInvalidatedLogoutPending && errorCode !== SESSION_INVALIDATED_CODE) {
          return Promise.reject(error);
        }

        switch (errorCode) {
          case 1011:
            if (!this.router.currentRoute.value.path.toLowerCase().startsWith('/account/login')) {
              this.router.push("/account/login");
            }
            break;

          case 1012:
            if (!this.router.currentRoute.value.path.toLowerCase().startsWith('/expired')) {
              this.router.push("/expired");
            }
            break;

          // Do not redirect to login page if an authentication error occurs in the shared links flow.
          case 1013: // Access link authentication failed
          case 1014: // Request link authentication failed
            console.log("Access or request link authentication failed");
            break;
          case 1015:
              if (!this.router.currentRoute.value.path.toLowerCase().startsWith('/account/logout')) {
                  this.router.push("/account/logout?error=accountNotFound");
              }
              break;

          case SESSION_INVALIDATED_CODE: {
            this.acknowledgeSessionInvalidatedLogout();
            break;
          }

          default:
            setLocation(this.router.currentRoute.value.fullPath);

            if (!this.router.currentRoute.value.path.toLowerCase().startsWith('/account/logout')) {
              this.router.push('/account/logout?error=sessionExpired');
            } else {
              this.router.push('/account/login?error=sessionExpired');
            }
            break;
        }
      }

      return Promise.reject(error);
    });
  }

  acknowledgeSessionInvalidatedLogout() {
    if (this.sessionInvalidatedLogoutPending) {
      import('@/stores/AuthStore.js')
        .then(({ useAuthStore }) => {
          const authStore = useAuthStore();
          if (!authStore.sessionInvalidatedDialogVisible) {
            authStore.showSessionInvalidatedLogout();
          }
        })
        .catch(() => {
          if (!this.router.currentRoute.value.path.toLowerCase().startsWith('/account/logout')) {
            this.router.push('/account/logout');
          }
        });
      return;
    }

    this.sessionInvalidatedLogoutPending = true;

    import('@/stores/AuthStore.js')
      .then(({ useAuthStore }) => {
        const authStore = useAuthStore();
        authStore.showSessionInvalidatedLogout();
      })
      .catch(() => {
        if (!this.router.currentRoute.value.path.toLowerCase().startsWith('/account/logout')) {
          this.router.push('/account/logout');
        }
      });
  }

  handleForbiddenNotFound() {
    this.axiosInstance.interceptors.response.use(undefined, (error) => {
      const mainStore = useMainStore();
      if (error && error.response && error.response.status == 403) {
        const snack = {
          i18n: "common.snackbars.resourceForbidden",
          color: "error",
        };

        if (error.config?.url === 'nodes') {
          snack.i18n = "common.snackbars.noTenantsRelated";
        }

        mainStore.createSnackbar(snack);
      }

      if (error && error.response && error.response.status == 404) {
        if (!error?.response?.data?.error?.message) {
          const snack = {
            i18n: "common.snackbars.resourceNotFound",
            color: "error",
          };

          mainStore.createSnackbar(snack);
        }
      }

      return Promise.reject(error);
    });
  }

  handleRatelimitExceeded() {
    this.axiosInstance.interceptors.response.use(undefined, (error) => {
      const mainStore = useMainStore();
      if (error && error.response && error.response.status == 429) {
        const snack = {
          i18n: "common.snackbars.ratelimitExceeded",
          color: "error",
        };

        mainStore.createSnackbar(snack);
      }

      return Promise.reject(error);
    });
  }

  handleBandwidthExceeded() {
    this.axiosInstance.interceptors.response.use(undefined, (error) => {
      if (
        error &&
        error.response?.status === 509 &&
        error.response?.data?.error?.status === 509
      ) {
        if (this.router && !this.router.currentRoute.value.path.toLowerCase().startsWith('/account/bandwidth-exceeded')) {
          this.router.push("/account/bandwidth-exceeded");
        }
      }

      return Promise.reject(error);
    });
  }
}

const axiosClient = new AxiosClient();

// Export the axios instance
const axiosInstance = axiosClient.axiosInstance;

export default axiosInstance;

// Export the axios client to allow router injection
export { axiosClient };
